import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Copy, ExternalLink, FileText, Files } from "lucide-react";
import TestLinkCard from "@/components/TestLinkCard";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import RulesDialog from "@/components/RulesDialog";
import BackgroundUploader from "@/components/BackgroundUploader";
import SegmentImageUploader from "@/components/SegmentImageUploader";
import DevicePreview from "@/components/DevicePreview";
import StatusPill from "@/components/StatusPill";
import ColorOverridePicker from "@/components/ColorOverridePicker";
import FontPicker from "@/components/FontPicker";
import AppearanceCustomizer from "@/components/AppearanceCustomizer";
import PredictionEditor from "@/components/PredictionEditor";
import GuessPriceEditor from "@/components/GuessPriceEditor";
import QuizEditor from "@/components/QuizEditor";
import QuizDrawPanel from "@/components/QuizDrawPanel";
import NinjaEditor from "@/components/NinjaEditor";
import BlowCandlesEditor from "@/components/BlowCandlesEditor";
import FlappyEditor from "@/components/FlappyEditor";
import MysteryBoxEditor from "@/components/mystery/MysteryBoxEditor";
import AdventEditor from "@/components/AdventEditor";
import GrandDrawPanel from "@/components/GrandDrawPanel";
import SpinWheel from "@/components/SpinWheel";
import { WheelDesignPicker } from "@/components/WheelDesignPicker";
import ProbabilityCalculator from "@/components/ProbabilityCalculator";
import DuplicateGameDialog from "@/components/DuplicateGameDialog";

// Compute game status locally (mirrors backend logic)
// GMT+4 timezone offset
function computeLocalStatus(game) {
  if (!game) return "live";
  if (!game.active) return "suspended";
  
  // "Now" in GMT+4 as "YYYY-MM-DDTHH:MM" — compared with date + optional hour
  const nowKey = new Date(Date.now() + 4 * 3600000).toISOString().slice(0, 16);

  const start = game.start_date ? `${game.start_date}T${game.start_time || "00:00"}` : null;
  const end = game.end_date ? `${game.end_date}T${game.end_time || "00:00"}` : null;

  if (start && nowKey < start) return "scheduled";
  if (end && nowKey >= end) return "ended";
  return "live";
}

// Instant-win types whose prizes can carry a picture shown inside the game
const PRIZE_IMAGE_TYPES = new Set(["slot_machine", "spin_wheel", "card_game", "mystery_box"]);

// Instant-win types eligible for the end-of-campaign grand draw
const GRAND_DRAW_TYPES = new Set([
  "spin_wheel",
  "scratch_card",
  "card_game",
  "slot_machine",
  "mystery_box",
  "blow_candles",
  "penalty_shootout",
  "registration_gift",
  "advent_calendar",
]);

const DEFAULT_CTA_TYPES = [
  "facebook_like",
  "instagram_follow",
  "tiktok_follow",
  "youtube_subscribe",
  "linkedin_follow",
  "google_review",
  "tripadvisor_review",
  "app_download",
  "newsletter_signup",
  "account_creation",
  "instagram_story",
];

const DAILY_PRIZE_TYPES = [
  "spin_wheel", "scratch_card", "slot_machine", "card_game",
  "blow_candles", "penalty_shootout", "mystery_box", "flappy",
];

export default function AdminGameEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [game, setGame] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  useEffect(() => {
    api.get(`/games/${id}`).then((r) => setGame(r.data));
    api.get("/merchants").then((r) => setMerchants(r.data.merchants));
  }, [id]);

  async function load() {
    const r = await api.get(`/games/${id}`);
    setGame(r.data);
  }

  // Track other active games on the same merchant (soft warning — choice B)
  useEffect(() => {
    if (!game?.id) return;
    if (!game.merchant_id || !game.active) {
      setSiblings([]);
      return;
    }
    api
      .get(`/games/${game.id}/sibling-active`)
      .then((r) => setSiblings(r.data.siblings || []))
      .catch(() => setSiblings([]));
  }, [game?.id, game?.merchant_id, game?.active]);

  function patch(partial) {
    setGame((g) => ({ ...g, ...partial }));
  }

  function patchFont(element, cfg) {
    setGame((g) => ({ ...g, fonts: { ...(g.fonts || {}), [element]: cfg } }));
  }

  async function save() {
    setSaving(true);
    try {
      const { id: _id, slug, plays_count, created_at, ...body } = game;
      await api.patch(`/games/${id}`, body);
      toast.success(t("game_editor.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function downloadTechnicalPdf() {
    try {
      const res = await api.get(`/games/${id}/technical-pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `QRISE-Fiche-${game?.slug || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function duplicateGame() {
    setDuplicateOpen(true);
  }

  async function performDuplicate(targetMerchantId) {
    setDuplicateLoading(true);
    try {
      const body = targetMerchantId ? { merchant_id: targetMerchantId } : {};
      const r = await api.post(`/games/${id}/duplicate`, body);
      toast.success(
        t("game_editor.duplicated", "Jeu dupliqué — édition du nouveau jeu"),
      );
      setDuplicateOpen(false);
      navigate(`/admin/games/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDuplicateLoading(false);
    }
  }

  async function handleTypeChange(newType) {
    if (!newType || newType === game.type) return;
    const plays = Number(game.plays_count || 0);
    const warningLines = [
      t(
        "game_editor.type_change_confirm_intro",
        "Vous êtes sur le point de changer le type de ce jeu.",
      ),
      "",
      t(
        "game_editor.type_change_confirm_kept",
        "✓ Lots, matchs, CTAs et autres réglages sont CONSERVÉS (le nouveau type ignore les champs qu'il n'utilise pas).",
      ),
    ];
    if (plays > 0) {
      warningLines.push("");
      warningLines.push(
        t("game_editor.type_change_confirm_warn_plays", {
          count: plays,
          defaultValue:
            "⚠️ {{count}} participations existent déjà sur ce jeu. Leurs résultats peuvent apparaître comme '—' ou avec un label incohérent dans les stats du nouveau type.",
        }),
      );
    }
    warningLines.push("");
    warningLines.push(
      t("game_editor.type_change_confirm_question", "Vraiment continuer ?"),
    );
    if (!window.confirm(warningLines.join("\n"))) return;
    try {
      const { data } = await api.patch(`/games/${id}`, { type: newType });
      setGame(data);
      toast.success(t("game_editor.type_change_done", "Type de jeu mis à jour"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function addSegment() {
    patch({
      segments: [
        ...(game.segments || []),
        {
          label: "Prize",
          color: "#8B5CF6",
          is_winning: true,
          weight: 1,
        },
      ],
    });
  }
  function updateSegment(i, partial) {
    const next = [...game.segments];
    next[i] = { ...next[i], ...partial };
    patch({ segments: next });
  }
  function removeSegment(i) {
    const next = game.segments.filter((_, idx) => idx !== i);
    patch({ segments: next });
  }
  function addCta() {
    patch({
      ctas: [
        ...(game.ctas || []),
        {
          type: "instagram_follow",
          label: "Follow us",
          url: "https://",
          required: true,
        },
      ],
    });
  }
  function updateCta(i, partial) {
    const next = [...game.ctas];
    next[i] = { ...next[i], ...partial };
    patch({ ctas: next });
  }
  function removeCta(i) {
    patch({ ctas: game.ctas.filter((_, idx) => idx !== i) });
  }

  if (!game)
    return (
      <div className="min-h-screen bg-muted/40">
        <Navbar variant="app" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">{t("dashboard.loading")}</div>
      </div>
    );

  const assignedMerchant = merchants.find((m) => m.id === game.merchant_id);
  const merchantSlug = assignedMerchant?.slug || null;
  // If the game is assigned to a merchant, the QR / public URL should be the
  // PERMANENT merchant URL (redirects to whichever game is active) — this is
  // what gets printed once and never reprinted.
  // We route through /api/share/* so social-media link previews get the
  // correct OG title/description per game type (e.g. registration_gift
  // surfaces "Inscrivez-vous en 30 secondes…" instead of "Jouez & gagnez").
  // Real users get redirected to /m/{slug} or /play/{slug} instantly.
  const publicUrl = merchantSlug
    ? `${window.location.origin}/api/share/m/${merchantSlug}`
    : `${window.location.origin}/api/share/play/${game.slug}`;
  const gamePreviewUrl = `${window.location.origin}/play/${game.slug}`;

  return (
    <div className="min-h-screen bg-muted/40">
      <Navbar variant="app" />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/admin")}
            data-testid="editor-back-btn"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> {t("common.back")}
          </button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-9"
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                toast.success(t("dashboard.copied"));
              }}
              data-testid="editor-copy-link"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              {t("dashboard.copy_link")}
            </Button>
            <a
              href={gamePreviewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-2 h-9"
              data-testid="editor-open-link"
              title={gamePreviewUrl}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-9"
              onClick={downloadTechnicalPdf}
              data-testid="editor-pdf-btn"
              title={t("game_editor.export_pdf_hint", "Télécharge la fiche technique PDF du jeu")}
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {t("game_editor.export_pdf", "Fiche PDF")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-9"
              onClick={duplicateGame}
              data-testid="editor-duplicate-btn"
              title={t("game_editor.duplicate_hint", "Crée une copie désactivée de ce jeu")}
            >
              <Files className="h-3.5 w-3.5 mr-1.5" />
              {t("game_editor.duplicate", "Dupliquer")}
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl h-9 bg-foreground text-background hover:bg-foreground/90"
              data-testid="editor-save-btn"
            >
              {saving ? "…" : t("game_editor.save")}
            </Button>
          </div>
        </div>

        <h1 className="text-3xl font-semibold tracking-tight">
          {game.title}
        </h1>
        <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
          <div>
            {merchantSlug
              ? t("game_editor.merchant_qr_link")
              : t("game_editor.public_link")}
            :{" "}
            <span className="font-mono" data-testid="editor-public-url">
              {publicUrl}
            </span>
          </div>
          {merchantSlug && (
            <div className="text-xs">
              {t("game_editor.merchant_qr_hint")}
            </div>
          )}
        </div>

        <div className="mt-4">
          <TestLinkCard gameId={game.id} testIdPrefix="editor-test-link" />
        </div>

        {/* Soft warning: another active game exists on the same merchant.
            The QR code will show whichever was most recently created. */}
        {siblings.length > 0 && (
          <div
            className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 text-amber-800 dark:text-amber-300 px-4 py-3 text-sm"
            data-testid="editor-sibling-warning"
          >
            <div className="font-semibold">
              {t("game_editor.sibling_warning_title")}
            </div>
            <div className="mt-1 text-xs leading-relaxed">
              {t("game_editor.sibling_warning_body", {
                count: siblings.length,
              })}
              <ul className="list-disc list-inside mt-1.5 space-y-0.5">
                {siblings.map((s) => (
                  <li key={s.id} className="font-mono">
                    {s.title}{" "}
                    <span className="opacity-70">({s.slug})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* Left: meta */}
          <div className="qrise-card p-6 space-y-4">
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.title")}
              </Label>
              <Input
                value={game.title || ""}
                onChange={(e) => patch({ title: e.target.value })}
                className="mt-1.5 rounded-xl h-10"
                data-testid="editor-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.type", "Type de jeu")}
              </Label>
              <Select
                value={game.type || "spin_wheel"}
                onValueChange={(v) => handleTypeChange(v)}
              >
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spin_wheel">
                    {t("landing.game_spin", "Roue de la fortune")}
                  </SelectItem>
                  <SelectItem value="scratch_card">
                    {t("landing.game_scratch", "Carte à gratter")}
                  </SelectItem>
                  <SelectItem value="card_game">
                    {t("landing.game_cards", "Cartes mystères")}
                  </SelectItem>
                  <SelectItem value="slot_machine">
                    {t("landing.game_slot", "Machine à sous")}
                  </SelectItem>
                  <SelectItem value="mystery_box">
                    {t("landing.game_mystery", "Boîte mystère")}
                  </SelectItem>
                  <SelectItem value="ninja">
                    {t("landing.game_ninja", "Ninja Slice")}
                  </SelectItem>
                  <SelectItem value="flappy">
                    {t("landing.game_flappy", "Flappy Mascotte")}
                  </SelectItem>
                  <SelectItem value="blow_candles">
                    {t("landing.game_blow_candles", "Souffle les bougies")}
                  </SelectItem>
                  <SelectItem value="prediction_match">
                    {t("landing.game_prediction", "Pronostic match")}
                  </SelectItem>
                  <SelectItem value="guess_price">
                    {t("landing.game_guess_price", "Devinez le prix")}
                  </SelectItem>
                  <SelectItem value="lottery_draw">
                    {t("landing.game_lottery", "Tirage au sort")}
                  </SelectItem>
                  <SelectItem value="quiz">
                    {t("landing.game_quiz", "Quiz")}
                  </SelectItem>
                  {game.type === "penalty_shootout" && (
                    <SelectItem value="penalty_shootout">
                      {t("landing.game_penalty", "Tir au but")}
                    </SelectItem>
                  )}
                  <SelectItem value="registration_gift">
                    {t("landing.game_registration_gift", "Inscription cadeau")}
                  </SelectItem>
                  <SelectItem value="advent_calendar">
                    {t("landing.game_avent", "Calendrier de l'Avent")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">
                {t(
                  "game_editor.type_hint",
                  "Changer le type conserve toute la configuration existante (lots, matchs, CTAs). Le nouveau type ignore simplement les champs qu'il n'utilise pas.",
                )}
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.description")}
              </Label>
              <Textarea
                value={game.description || ""}
                onChange={(e) => patch({ description: e.target.value })}
                className="mt-1.5 rounded-xl"
                rows={3}
                data-testid="editor-description-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.assign")}
              </Label>
              <Select
                value={game.merchant_id || "none"}
                onValueChange={(v) =>
                  patch({ merchant_id: v === "none" ? null : v })
                }
              >
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-merchant-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("game_editor.none")}</SelectItem>
                  {merchants.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between py-1">
              <Label className="text-sm font-medium">
                {t("game_editor.active")}
              </Label>
              <Switch
                checked={!!game.active}
                onCheckedChange={(v) => patch({ active: v })}
                data-testid="editor-active-switch"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium">
                  {t("game_editor.start_date")}
                </Label>
                <Input
                  type="date"
                  value={game.start_date || ""}
                  onChange={(e) =>
                    patch({ start_date: e.target.value || null })
                  }
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-start-date-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">
                  {t("game_editor.end_date")}
                </Label>
                <Input
                  type="date"
                  value={game.end_date || ""}
                  onChange={(e) => patch({ end_date: e.target.value || null })}
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-end-date-input"
                  min={game.start_date || undefined}
                />
              </div>
              {[
                ["start_time", "editor-start-time-input"],
                ["end_time", "editor-end-time-input"],
              ].map(([field, testId]) => (
                <div key={field}>
                  <Label className="text-xs font-medium">{t(`game_editor.${field}`)}</Label>
                  <Input
                    type="time"
                    value={game[field] || ""}
                    onChange={(e) => patch({ [field]: e.target.value || null })}
                    className="mt-1.5 rounded-xl h-10"
                    data-testid={testId}
                  />
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                {t("game_editor.computed_status")}
              </div>
              <StatusPill status={computeLocalStatus(game)} />
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              {t("game_editor.date_hint")}
            </p>

            {/* Coupon "usable from" — only for direct-win game types */}
            {["spin_wheel", "scratch_card", "card_game", "slot_machine", "blow_candles"].includes(
              game.type
            ) && (
              <div
                className="rounded-xl border border-border/60 p-3 space-y-2"
                data-testid="usable-from-block"
              >
                <Label className="text-xs font-medium">
                  {t("game_editor.usable_from")}
                </Label>
                <Select
                  value={game.usable_from_mode || "tomorrow"}
                  onValueChange={(v) => patch({ usable_from_mode: v })}
                >
                  <SelectTrigger
                    className="rounded-xl h-10"
                    data-testid="usable-from-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediately">
                      {t("game_editor.usable_immediately")}
                    </SelectItem>
                    <SelectItem value="tomorrow">
                      {t("game_editor.usable_tomorrow")}
                    </SelectItem>
                    <SelectItem value="next_monday">
                      {t("game_editor.usable_next_monday")}
                    </SelectItem>
                    <SelectItem value="custom">
                      {t("game_editor.usable_custom")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {game.usable_from_mode === "custom" && (
                  <Input
                    type="date"
                    value={game.usable_from_date || ""}
                    onChange={(e) =>
                      patch({ usable_from_date: e.target.value || null })
                    }
                    className="rounded-xl h-10"
                    data-testid="usable-from-date"
                  />
                )}
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {t("game_editor.usable_from_hint")}
                </p>
              </div>
            )}

            {game.type === "lottery_draw" && (
              <div>
                <Label className="text-xs font-medium">
                  {t("game_editor.winners_count")}
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={game.winners_count ?? 1}
                  onChange={(e) =>
                    patch({
                      winners_count: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-winners-count-input"
                />
                <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">
                  {t("game_editor.winners_count_hint")}
                </p>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.language")}
              </Label>
              <Select
                value={game.language || "en"}
                onValueChange={(v) => patch({ language: v })}
              >
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="editor-language-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">
                    {t("game_editor.language_en")}
                  </SelectItem>
                  <SelectItem value="fr">
                    {t("game_editor.language_fr")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.win_message")}
              </Label>
              <Textarea
                value={game.win_message || ""}
                onChange={(e) => patch({ win_message: e.target.value })}
                className="mt-1.5 rounded-xl"
                rows={2}
                data-testid="editor-win-message-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.lose_message")}
              </Label>
              <Textarea
                value={game.lose_message || ""}
                onChange={(e) => patch({ lose_message: e.target.value })}
                className="mt-1.5 rounded-xl"
                rows={2}
                data-testid="editor-lose-message-input"
              />
            </div>
            {(game.type === "lottery_draw" || game.type === "quiz") && (
              <div className="rounded-xl border border-border/60 p-3 space-y-3" data-testid="editor-confirm-texts">
                <div className="text-[11px] text-muted-foreground leading-snug">
                  {t("game_editor.confirm_hint")}
                </div>
                <div>
                  <Label className="text-xs font-medium">{t("game_editor.confirm_title")}</Label>
                  <Input
                    value={game.confirm_title || ""}
                    onChange={(e) => patch({ confirm_title: e.target.value || null })}
                    placeholder={t("play.lottery_confirmed_title")}
                    className="mt-1.5 rounded-xl h-10"
                    data-testid="editor-confirm-title-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium">{t("game_editor.confirm_body")}</Label>
                  <Textarea
                    value={game.confirm_body || ""}
                    onChange={(e) => patch({ confirm_body: e.target.value || null })}
                    placeholder={t("play.lottery_confirmed_body")}
                    className="mt-1.5 rounded-xl"
                    rows={3}
                    data-testid="editor-confirm-body-input"
                  />
                </div>
                {game.type === "quiz" && (
                  <>
                    <div className="text-[11px] text-muted-foreground leading-snug pt-1 border-t border-border/60">
                      {t("game_editor.quiz_intro_hint")}
                    </div>
                    <div>
                      <Label className="text-xs font-medium">{t("game_editor.quiz_intro_text")}</Label>
                      <Textarea
                        value={game.quiz_intro_text || ""}
                        onChange={(e) => patch({ quiz_intro_text: e.target.value || null })}
                        placeholder={t("play.quiz_intro_body")}
                        className="mt-1.5 rounded-xl"
                        rows={3}
                        data-testid="editor-quiz-intro-text-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">{t("game_editor.quiz_winners_label")}</Label>
                      <Input
                        value={game.quiz_winners_label || ""}
                        onChange={(e) => patch({ quiz_winners_label: e.target.value || null })}
                        placeholder={t("play.quiz_intro_winners", { count: game.winners_count || 1 })}
                        className="mt-1.5 rounded-xl h-10"
                        data-testid="editor-quiz-winners-label-input"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs font-medium">
                {t(
                  "game_editor.win_email_message",
                  "Message personnalisé dans l'email de gain"
                )}
              </Label>
              <Textarea
                value={game.win_email_message || ""}
                onChange={(e) =>
                  patch({ win_email_message: e.target.value || null })
                }
                className="mt-1.5 rounded-xl"
                rows={3}
                maxLength={500}
                placeholder={t(
                  "game_editor.win_email_message_placeholder",
                  "Ex : Passez récupérer votre lot au 15 rue de la Liberté, entre 14h et 19h. Présentez votre code à l'accueil."
                )}
                data-testid="editor-win-email-message-input"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t(
                  "game_editor.win_email_message_hint",
                  "Affiché dans l'email envoyé au gagnant (sous le bouton « Je récupère mon lot »). Max 500 caractères. Laissez vide pour ne pas afficher ce bloc."
                )}
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.rules_url")}
              </Label>
              <Input
                type="url"
                value={game.rules_url || ""}
                onChange={(e) =>
                  patch({ rules_url: e.target.value || null })
                }
                placeholder="https://"
                className="mt-1.5 rounded-xl h-10"
                data-testid="editor-rules-url-input"
              />
              <div className="mt-1 text-[10px] text-muted-foreground">
                {t("game_editor.rules_url_hint")}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRulesOpen(true)}
                className="mt-3 w-full rounded-xl h-9"
                data-testid="editor-rules-manage-btn"
              >
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                {t("rules.manage")}
              </Button>
            </div>
            <div
              className="pt-2 border-t border-border/60"
              data-testid="backgrounds-section"
            >
              <label className="text-xs font-medium block mb-1">
                {t("bg.responsive_title", "Images de fond par appareil")}
              </label>
              <p className="text-[11px] text-muted-foreground leading-snug mb-3">
                {t(
                  "bg.responsive_hint",
                  "Le joueur voit automatiquement l'image adaptée à son écran. L'image PC sert de repli si aucune image tablette ou mobile n'est définie."
                )}
              </p>
              <div className="flex flex-wrap gap-5">
                <BackgroundUploader
                  variant="desktop"
                  label={t("bg.label_desktop", "Fond PC (par défaut)")}
                  value={game.background_image}
                  onChange={(v) => patch({ background_image: v })}
                />
                <BackgroundUploader
                  variant="tablet"
                  label={t("bg.label_tablet", "Fond Tablette")}
                  value={game.background_image_tablet}
                  onChange={(v) => patch({ background_image_tablet: v })}
                  testIdPrefix="bg-tablet"
                />
                <BackgroundUploader
                  variant="mobile"
                  label={t("bg.label_mobile", "Fond Mobile")}
                  value={game.background_image_mobile}
                  onChange={(v) => patch({ background_image_mobile: v })}
                  testIdPrefix="bg-mobile"
                />
              </div>
              <div className="mt-5 pt-4 border-t border-border/60">
                <label className="text-xs font-medium block mb-1">
                  {t("bg.preview_title", "Aperçu joueur")}
                </label>
                <p className="text-[11px] text-muted-foreground leading-snug mb-3">
                  {t(
                    "bg.preview_hint",
                    "Rendu approximatif de la page de jeu publique avec vos fonds, couleurs et polices."
                  )}
                </p>
                <DevicePreview game={game} />
              </div>
            </div>
            <div
              className="pt-2 border-t border-border/60"
              data-testid="text-colors-section"
            >
              <label className="text-xs font-medium block mb-1">
                {t("game_editor.text_colors")}
              </label>
              <p className="text-[11px] text-muted-foreground leading-snug mb-3">
                {t("game_editor.text_colors_hint")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <ColorOverridePicker
                  label={t("game_editor.title_color")}
                  value={game.title_color}
                  onChange={(v) => patch({ title_color: v })}
                  previewText={game.title || "Titre"}
                  testIdPrefix="title-color"
                />
                <ColorOverridePicker
                  label={t("game_editor.description_color")}
                  value={game.description_color}
                  onChange={(v) => patch({ description_color: v })}
                  previewText={
                    game.description
                      ? game.description.slice(0, 28) + "…"
                      : "Description"
                  }
                  testIdPrefix="description-color"
                />
              </div>
            </div>

            <div
              className="pt-2 border-t border-border/60"
              data-testid="fonts-section"
            >
              <label className="text-xs font-medium block mb-1">
                {t("game_editor.fonts_section", "Typographie")}
              </label>
              <p className="text-[11px] text-muted-foreground leading-snug mb-3">
                {t(
                  "game_editor.fonts_hint",
                  "Choisissez une police pour chaque élément de la page de jeu publique. Laissez « Par défaut » pour conserver la police du thème."
                )}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FontPicker
                  label={t("game_editor.font_title", "Titre")}
                  value={game.fonts?.title}
                  onChange={(v) => patchFont("title", v)}
                  previewText={game.title || "Titre"}
                  testIdPrefix="font-title"
                />
                <FontPicker
                  label={t("game_editor.font_description", "Description")}
                  value={game.fonts?.description}
                  onChange={(v) => patchFont("description", v)}
                  previewText={
                    game.description
                      ? game.description.slice(0, 28) + "…"
                      : "Description"
                  }
                  testIdPrefix="font-description"
                />
                <FontPicker
                  label={t("game_editor.font_prizes", "Libellés des lots")}
                  value={game.fonts?.prizes}
                  onChange={(v) => patchFont("prizes", v)}
                  previewText={
                    game.segments?.[0]?.label || "Lot gagnant"
                  }
                  testIdPrefix="font-prizes"
                />
                <FontPicker
                  label={t(
                    "game_editor.font_messages",
                    "Messages gagné / perdu"
                  )}
                  value={game.fonts?.messages}
                  onChange={(v) => patchFont("messages", v)}
                  previewText={game.win_message || "Félicitations !"}
                  testIdPrefix="font-messages"
                />
                <FontPicker
                  label={t("game_editor.font_buttons", "Boutons")}
                  value={game.fonts?.buttons}
                  onChange={(v) => patchFont("buttons", v)}
                  previewText="Continuer →"
                  testIdPrefix="font-buttons"
                />
                <FontPicker
                  label={t("game_editor.font_cta", "Actions (CTA)")}
                  value={game.fonts?.cta}
                  onChange={(v) => patchFont("cta", v)}
                  previewText={game.ctas?.[0]?.label || "Suivez-nous"}
                  testIdPrefix="font-cta"
                />
              </div>
            </div>

            <div
              className="pt-2 border-t border-border/60"
              data-testid="appearance-section"
            >
              <label className="text-xs font-medium block mb-1">
                {t("game_editor.appearance_section", "Boutons & effets")}
              </label>
              <AppearanceCustomizer
                game={game}
                patch={patch}
                testIdPrefix="appearance"
              />
            </div>

            {/* ------------------- One prize per day ------------------- */}
            {DAILY_PRIZE_TYPES.includes(game.type) && !(game.type === "flappy" && game.flappy_mode === "tournament") && (
              <div className="pt-2 border-t border-border/60" data-testid="daily-prize-section">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium">{t("game_editor.daily_prize_title")}</label>
                  <Switch
                    checked={!!game.daily_prize}
                    onCheckedChange={(v) => patch({ daily_prize: !!v })}
                    data-testid="daily-prize-toggle"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                  {t("game_editor.daily_prize_hint")}
                </p>
                {game.daily_prize && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-[11px] text-muted-foreground" htmlFor="daily-win-limit">
                      {t("game_editor.daily_win_limit")}
                    </label>
                    <Input
                      id="daily-win-limit"
                      type="number"
                      min={1}
                      max={1000}
                      value={game.daily_win_limit ?? 1}
                      onChange={(e) => patch({ daily_win_limit: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                      className="rounded-xl h-8 w-20"
                      data-testid="daily-win-limit-input"
                    />
                  </div>
                )}
                {game.daily_prize && (game.segments || []).every((s) => s.is_winning) && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-snug mt-1.5" data-testid="daily-prize-needs-losing">
                    {t("errors.daily_prize_needs_losing")}
                  </p>
                )}
              </div>
            )}

            {/* ------------------- Proof of purchase ------------------- */}
            <div
              className="pt-2 border-t border-border/60"
              data-testid="purchase-proof-section"
            >
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium">
                  {t("game_editor.purchase_proof_title")}
                </label>
                <Switch
                  checked={!!game.requires_purchase_proof}
                  onCheckedChange={(v) =>
                    patch({ requires_purchase_proof: !!v })
                  }
                  data-testid="purchase-proof-toggle"
                />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                {t("game_editor.purchase_proof_hint")}
              </p>

              {game.requires_purchase_proof && (
                <div className="mt-3 space-y-3 rounded-lg border border-border/60 bg-muted/40 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("game_editor.purchase_filters")}
                  </div>

                  <div>
                    <label className="text-[11px] text-muted-foreground block mb-1">
                      {t("game_editor.purchase_min_amount")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        game.purchase_min_amount === null ||
                        game.purchase_min_amount === undefined
                          ? ""
                          : game.purchase_min_amount
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        patch({
                          purchase_min_amount:
                            v === "" ? null : parseFloat(v),
                        });
                      }}
                      placeholder={t("game_editor.purchase_min_amount_placeholder")}
                      className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
                      data-testid="purchase-min-amount-input"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] text-muted-foreground block mb-1">
                      {t("game_editor.purchase_max_days")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={
                        game.purchase_max_days === null ||
                        game.purchase_max_days === undefined
                          ? ""
                          : game.purchase_max_days
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        patch({
                          purchase_max_days:
                            v === "" ? null : parseInt(v, 10),
                        });
                      }}
                      placeholder={t("game_editor.purchase_max_days_placeholder")}
                      className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
                      data-testid="purchase-max-days-input"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] text-muted-foreground block mb-1">
                      {t("game_editor.purchase_merchant_keyword")}
                    </label>
                    <input
                      type="text"
                      value={game.purchase_merchant_keyword || ""}
                      onChange={(e) =>
                        patch({
                          purchase_merchant_keyword: e.target.value || null,
                        })
                      }
                      placeholder={t(
                        "game_editor.purchase_merchant_keyword_placeholder"
                      )}
                      className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
                      data-testid="purchase-merchant-keyword-input"
                    />
                  </div>

                  <p className="text-[11px] text-muted-foreground/80 leading-snug">
                    {t("game_editor.purchase_filters_hint")}
                  </p>
                </div>
              )}
            </div>

            {/* ------------------- Shipping address ------------------- */}
            <div
              className="pt-2 border-t border-border/60"
              data-testid="shipping-address-section"
            >
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium">
                  {t("game_editor.shipping_address_title")}
                </label>
                <Switch
                  checked={!!game.requires_shipping_address}
                  onCheckedChange={(v) =>
                    patch({ requires_shipping_address: !!v })
                  }
                  data-testid="shipping-address-toggle"
                />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                {t("game_editor.shipping_address_hint")}
              </p>
            </div>

            {game.type === "spin_wheel" && <WheelDesignPicker game={game} patch={patch} />}

            {/* ------------- Spin Wheel: label orientation ------------- */}
            {game.type === "spin_wheel" && game.wheel_design !== "premium" && (
              <div
                className="pt-2 border-t border-border/60"
                data-testid="wheel-label-orientation-section"
              >
                <label className="text-xs font-medium block mb-1">
                  {t("game_editor.wheel_label_orientation")}
                </label>
                <p className="text-[11px] text-muted-foreground leading-snug mb-2">
                  {t("game_editor.wheel_label_orientation_hint")}
                </p>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={game.wheel_label_orientation || "horizontal"}
                      onValueChange={(v) =>
                        patch({ wheel_label_orientation: v })
                      }
                    >
                      <SelectTrigger
                        className="rounded-xl h-10"
                        data-testid="wheel-label-orientation-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="horizontal"
                          data-testid="wheel-label-orientation-horizontal"
                        >
                          {t("game_editor.wheel_label_horizontal")}
                        </SelectItem>
                        <SelectItem
                          value="vertical"
                          data-testid="wheel-label-orientation-vertical"
                        >
                          {t("game_editor.wheel_label_vertical")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground/80 leading-snug mt-2">
                      {t("game_editor.wheel_label_preview_caption")}
                    </p>
                  </div>
                  <div
                    className="w-[140px] shrink-0"
                    data-testid="wheel-label-orientation-preview"
                  >
                    <SpinWheel
                      segments={game.segments || []}
                      labelOrientation={
                        game.wheel_label_orientation || "horizontal"
                      }
                      preview
                    />
                  </div>
                </div>
              </div>
            )}
            {["scratch_card", "card_game"].includes(game.type) && (
              <div
                className="pt-2 border-t border-border/60"
                data-testid="card-back-color-section"
              >
                <label className="text-xs font-medium block mb-1">
                  {game.type === "scratch_card"
                    ? t("game_editor.scratch_color")
                    : t("game_editor.card_back_color")}
                </label>
                <p className="text-[11px] text-muted-foreground leading-snug mb-2">
                  {game.type === "scratch_card"
                    ? t("game_editor.scratch_color_hint")
                    : t("game_editor.card_back_color_hint")}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={
                      (game.type === "scratch_card"
                        ? game.scratch_color
                        : game.card_back_color) || "#1d2230"
                    }
                    onChange={(e) =>
                      patch(
                        game.type === "scratch_card"
                          ? { scratch_color: e.target.value }
                          : { card_back_color: e.target.value }
                      )
                    }
                    className="h-10 w-14 rounded-lg border border-border cursor-pointer bg-transparent"
                    data-testid="card-back-color-picker"
                  />
                  <input
                    type="text"
                    value={
                      (game.type === "scratch_card"
                        ? game.scratch_color
                        : game.card_back_color) || "#1d2230"
                    }
                    onChange={(e) =>
                      patch(
                        game.type === "scratch_card"
                          ? { scratch_color: e.target.value }
                          : { card_back_color: e.target.value }
                      )
                    }
                    placeholder="#1d2230"
                    className="flex-1 h-10 rounded-lg border border-border bg-background px-3 text-xs font-mono"
                    data-testid="card-back-color-hex"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[
                    "#1d2230",
                    "#7c2d12",
                    "#1e3a8a",
                    "#14532d",
                    "#581c87",
                    "#4c1d95",
                    "#9f1239",
                    "#111827",
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() =>
                        patch(
                          game.type === "scratch_card"
                            ? { scratch_color: preset }
                            : { card_back_color: preset }
                        )
                      }
                      className="h-7 w-7 rounded-full border-2 border-white/60 shadow-sm transition-transform hover:scale-110"
                      style={{ background: preset }}
                      data-testid={`card-back-preset-${preset.slice(1)}`}
                      title={preset}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Middle: segments (hidden for lottery_draw + prediction_match + guess_price) */}
          {game.type === "prediction_match" && (
            <PredictionEditor
              game={game}
              onGameChange={(p) => patch(p)}
              onReload={load}
            />
          )}
          {game.type === "guess_price" && (
            <GuessPriceEditor
              game={game}
              onGameChange={(p) => patch(p)}
              onReload={load}
            />
          )}
          {game.type === "quiz" && (
            <>
              <QuizEditor game={game} onGameChange={(p) => patch(p)} />
              <QuizDrawPanel game={game} />
            </>
          )}
          {game.type === "ninja" && (
            <NinjaEditor
              game={game}
              onGameChange={(p) => patch(p)}
              onReload={load}
            />
          )}
          {game.type === "blow_candles" && (
            <BlowCandlesEditor
              game={game}
              onGameChange={(p) => patch(p)}
            />
          )}
          {game.type === "flappy" && (
            <FlappyEditor
              game={game}
              onGameChange={(p) => patch(p)}
            />
          )}
          {game.type === "mystery_box" && (
            <MysteryBoxEditor
              game={game}
              onGameChange={(p) => patch(p)}
            />
          )}
          {game.type === "advent_calendar" && (
            <AdventEditor
              game={game}
              onGameChange={(p) => patch(p)}
            />
          )}
          {GRAND_DRAW_TYPES.has(game.type) && (
            <GrandDrawPanel game={game} onGameChange={(p) => patch(p)} />
          )}
          {game.type !== "lottery_draw" &&
            game.type !== "prediction_match" &&
            game.type !== "guess_price" &&
            game.type !== "quiz" &&
            game.type !== "advent_calendar" &&
            game.type !== "ninja" && (
          <div className="qrise-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold">
                {game.type === "scratch_card" ||
                game.type === "card_game" ||
                game.type === "slot_machine"
                  ? t("game_editor.prize_pool")
                  : t("game_editor.segments")}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addSegment}
                className="h-8 rounded-lg text-xs"
                data-testid="editor-add-segment-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("game_editor.add_segment")}
              </Button>
            </div>
            <ProbabilityCalculator
              segments={game.segments || []}
              onApply={(next) => patch({ segments: next })}
            />
            {(() => {
              const segs = game.segments || [];
              const sum = segs.reduce(
                (a, s) => a + (parseFloat(s.weight) || 0),
                0
              );
              const rounded = Math.round(sum * 10) / 10;
              const ok = Math.abs(rounded - 100) < 0.1;
              return (
                <div
                  className={`mb-3 flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs ${
                    ok
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                      : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  }`}
                  data-testid="segments-sum-banner"
                >
                  <span className="font-medium">
                    {ok
                      ? t("game_editor.sum_ok")
                      : t("game_editor.sum_warning", { sum: rounded })}
                  </span>
                  {!ok && segs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (sum <= 0) return;
                        const next = segs.map((s) => ({
                          ...s,
                          weight:
                            Math.round(
                              ((parseFloat(s.weight) || 0) / sum) * 1000
                            ) / 10,
                        }));
                        // fix rounding so total is exactly 100
                        const total = next.reduce(
                          (a, s) => a + s.weight,
                          0
                        );
                        const diff = Math.round((100 - total) * 10) / 10;
                        if (next.length > 0) next[0].weight += diff;
                        patch({ segments: next });
                      }}
                      className="shrink-0 rounded-lg bg-foreground text-background px-2.5 py-1 font-semibold hover:opacity-90"
                      data-testid="segments-normalize-btn"
                    >
                      {t("game_editor.normalize_sum")}
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="space-y-3 max-h-[560px] overflow-auto pr-1">
              {(game.segments || []).map((s, i) => {
                const segs = game.segments || [];
                const depleted =
                  s.initial_quantity != null &&
                  (parseInt(s.remaining_quantity) || 0) <= 0;
                const effectiveSum = segs.reduce((a, x) => {
                  const d =
                    x.initial_quantity != null &&
                    (parseInt(x.remaining_quantity) || 0) <= 0;
                  return a + (d ? 0 : parseFloat(x.weight) || 0);
                }, 0);
                const actual = depleted
                  ? 0
                  : effectiveSum > 0
                    ? Math.round(
                        ((parseFloat(s.weight) || 0) / effectiveSum) * 1000
                      ) / 10
                    : 0;
                return (
                <div
                  key={i}
                  className={`p-3 rounded-xl border bg-background/50 ${
                    depleted ? "border-destructive/40" : "border-border"
                  }`}
                  data-testid={`segment-row-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={s.color}
                      onChange={(e) =>
                        updateSegment(i, { color: e.target.value })
                      }
                      className="h-9 w-9 rounded-lg border border-border cursor-pointer"
                      data-testid={`segment-color-${i}`}
                    />
                    <Input
                      value={s.label}
                      onChange={(e) =>
                        updateSegment(i, { label: e.target.value })
                      }
                      className="h-9 rounded-lg flex-1"
                      data-testid={`segment-label-${i}`}
                    />
                    <button
                      onClick={() => removeSegment(i)}
                      className="h-9 w-9 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
                      data-testid={`segment-delete-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {PRIZE_IMAGE_TYPES.has(game.type) && (
                    <SegmentImageUploader
                      value={s.image_url}
                      onChange={(url) => updateSegment(i, { image_url: url })}
                      testId={`segment-image-${i}`}
                    />
                  )}
                  {depleted && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                      data-testid={`segment-depleted-${i}`}
                    >
                      {t("game_editor.out_of_stock")}
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!s.is_winning}
                        onCheckedChange={(v) =>
                          updateSegment(i, { is_winning: v })
                        }
                        data-testid={`segment-win-${i}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        {t("game_editor.winning")}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            step="0.1"
                            min={0}
                            max={100}
                            value={s.weight}
                            onChange={(e) =>
                              updateSegment(i, {
                                weight:
                                  e.target.value === ""
                                    ? 0
                                    : parseFloat(e.target.value),
                              })
                            }
                            className="h-8 rounded-lg pr-7 text-right"
                            data-testid={`segment-chance-${i}`}
                            aria-label={t("game_editor.chance")}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                            %
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground tracking-wide">
                        {t("game_editor.chance")} ·{" "}
                        <span
                          className={`font-semibold ${
                            depleted
                              ? "text-destructive"
                              : "text-foreground/80"
                          }`}
                        >
                          {actual}%
                        </span>{" "}
                        {t("game_editor.actual_chance")}
                      </div>
                    </div>
                  </div>

                  {s.is_winning && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                        {t("game_editor.prize_details")}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground">
                            {t("game_editor.initial_qty")}
                          </label>
                          <Input
                            type="number"
                            min={0}
                            value={
                              s.initial_quantity == null
                                ? ""
                                : s.initial_quantity
                            }
                            placeholder={t("game_editor.unlimited")}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "") {
                                updateSegment(i, {
                                  initial_quantity: null,
                                  remaining_quantity: null,
                                });
                              } else {
                                const n = Math.max(0, parseInt(v) || 0);
                                const prevInit = s.initial_quantity;
                                const prevRem = s.remaining_quantity;
                                updateSegment(i, {
                                  initial_quantity: n,
                                  remaining_quantity:
                                    prevInit == null || prevRem == null
                                      ? n
                                      : Math.min(prevRem, n),
                                });
                              }
                            }}
                            className="h-8 rounded-lg text-sm"
                            data-testid={`segment-initial-qty-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">
                            {t("game_editor.remaining_qty")}
                          </label>
                          <Input
                            type="number"
                            min={0}
                            value={
                              s.remaining_quantity == null
                                ? ""
                                : s.remaining_quantity
                            }
                            placeholder={t("game_editor.unlimited")}
                            disabled={s.initial_quantity == null}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateSegment(i, {
                                remaining_quantity:
                                  v === ""
                                    ? null
                                    : Math.max(0, parseInt(v) || 0),
                              });
                            }}
                            className="h-8 rounded-lg text-sm"
                            data-testid={`segment-remaining-qty-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">
                            {t("game_editor.claim_days")}
                          </label>
                          <Input
                            type="number"
                            min={0}
                            value={s.claim_days == null ? "" : s.claim_days}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateSegment(i, {
                                claim_days:
                                  v === ""
                                    ? null
                                    : Math.max(0, parseInt(v) || 0),
                              });
                            }}
                            className="h-8 rounded-lg text-sm"
                            data-testid={`segment-claim-days-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">
                            {t("game_editor.claim_deadline")}
                          </label>
                          <Input
                            type="date"
                            value={s.claim_deadline || ""}
                            onChange={(e) =>
                              updateSegment(i, {
                                claim_deadline: e.target.value || null,
                              })
                            }
                            className="h-8 rounded-lg text-sm"
                            data-testid={`segment-deadline-${i}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">
                          {t("game_editor.coupon")}
                        </label>
                        <Input
                          value={s.coupon_code || ""}
                          onChange={(e) =>
                            updateSegment(i, {
                              coupon_code: e.target.value || null,
                            })
                          }
                          placeholder="SUMMER10"
                          className="h-8 rounded-lg text-sm font-mono uppercase"
                          data-testid={`segment-coupon-${i}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">
                          {t("game_editor.redemption_url")}
                        </label>
                        <Input
                          type="url"
                          value={s.redemption_url || ""}
                          onChange={(e) =>
                            updateSegment(i, {
                              redemption_url: e.target.value || null,
                            })
                          }
                          placeholder="https://"
                          className="h-8 rounded-lg text-sm"
                          data-testid={`segment-url-${i}`}
                        />
                      </div>
                      {s.coupon_code && s.redemption_url && (
                        <div className="rounded-xl border border-violet-300/50 bg-violet-50/40 dark:bg-violet-500/5 p-3 space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <div className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-violet-700 dark:text-violet-300">
                              {t("game_editor.promo_mode_badge")}
                            </div>
                          </div>
                          <label className="text-[10px] text-muted-foreground">
                            {t("game_editor.promo_description")}
                          </label>
                          <Textarea
                            value={s.promo_description || ""}
                            onChange={(e) =>
                              updateSegment(i, {
                                promo_description: e.target.value || null,
                              })
                            }
                            placeholder={t(
                              "game_editor.promo_description_placeholder"
                            )}
                            rows={3}
                            className="rounded-lg text-sm"
                            data-testid={`segment-promo-description-${i}`}
                          />
                          <p className="text-[11px] text-muted-foreground leading-snug">
                            {t("game_editor.promo_description_hint")}
                          </p>
                        </div>
                      )}
                      {/* Per-prize email instructions — appears in the
                          winning email, on top of the game-level message.
                          Visible on all winning segments (no condition). */}
                      <div className="rounded-xl border border-amber-300/50 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-amber-700 dark:text-amber-300">
                            {t("game_editor.prize_instructions_badge", "Email — info sur ce lot")}
                          </div>
                        </div>
                        <Textarea
                          value={s.instructions || ""}
                          onChange={(e) =>
                            updateSegment(i, {
                              instructions: e.target.value || null,
                            })
                          }
                          placeholder={t(
                            "game_editor.prize_instructions_placeholder",
                            "Ex : Valable uniquement le mardi de 14h à 17h, sur place."
                          )}
                          rows={2}
                          className="rounded-lg text-sm"
                          data-testid={`segment-instructions-${i}`}
                        />
                        <p className="text-[11px] text-muted-foreground leading-snug">
                          {t(
                            "game_editor.prize_instructions_hint",
                            "Apparaît dans l'email de gain sous le code, en plus du message global du jeu."
                          )}
                        </p>
                      </div>
                      {/* Per-prize popup info — appears in the IN-APP win
                          pop-up, distinct from the email instructions. */}
                      <div className="rounded-xl border border-sky-300/50 bg-sky-50/50 dark:bg-sky-500/5 p-3 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-sky-700 dark:text-sky-300">
                            {t("game_editor.prize_popup_badge", "Détails supplémentaires")}
                          </div>
                        </div>
                        <Textarea
                          value={s.popup_info || ""}
                          onChange={(e) =>
                            updateSegment(i, {
                              popup_info: e.target.value || null,
                            })
                          }
                          placeholder={t(
                            "game_editor.prize_popup_placeholder",
                            "Ex : Rendez-vous en boutique à Paris 15e avec votre email pour récupérer ton lot."
                          )}
                          rows={2}
                          className="rounded-lg text-sm"
                          data-testid={`segment-popup-info-${i}`}
                        />
                        <p className="text-[11px] text-muted-foreground leading-snug">
                          {t(
                            "game_editor.prize_popup_hint",
                            "S'affiche dans la pop-up de gain à l'écran, immédiatement après la partie."
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Right: CTAs */}
          <div className="qrise-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold">
                {t("game_editor.ctas")}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addCta}
                className="h-8 rounded-lg text-xs"
                data-testid="editor-add-cta-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("game_editor.add_cta")}
              </Button>
            </div>
            <div className="space-y-3 max-h-[560px] overflow-auto pr-1">
              {(game.ctas || []).map((c, i) => (
                <div
                  key={i}
                  className="p-3 rounded-xl border border-border bg-background/50 space-y-2"
                  data-testid={`cta-row-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <Select
                      value={c.type}
                      onValueChange={(v) => updateCta(i, { type: v })}
                    >
                      <SelectTrigger
                        className="h-9 rounded-lg flex-1"
                        data-testid={`cta-type-${i}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEFAULT_CTA_TYPES.map((ct) => (
                          <SelectItem key={ct} value={ct}>
                            {ct.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => removeCta(i)}
                      className="h-9 w-9 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
                      data-testid={`cta-delete-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Input
                    value={c.label}
                    onChange={(e) => updateCta(i, { label: e.target.value })}
                    className="h-9 rounded-lg"
                    placeholder={t("game_editor.label")}
                    data-testid={`cta-label-${i}`}
                  />
                  <Input
                    value={c.instruction || ""}
                    onChange={(e) => updateCta(i, { instruction: e.target.value || null })}
                    className="h-9 rounded-lg"
                    placeholder={t(`play.cta_inst_${c.type}`, { defaultValue: t("game_editor.cta_instruction") })}
                    title={t("game_editor.cta_instruction_hint")}
                    data-testid={`cta-instruction-${i}`}
                  />
                  <Input
                    value={c.url}
                    onChange={(e) => updateCta(i, { url: e.target.value })}
                    className="h-9 rounded-lg"
                    placeholder={
                      c.type === "app_download" ? "https:// (fallback)" : "https://"
                    }
                    data-testid={`cta-url-${i}`}
                  />
                  {c.type === "app_download" && (
                    <div className="grid grid-cols-1 gap-2 pt-1">
                      <div>
                        <label className="text-[10px] text-muted-foreground font-medium">
                          {t("game_editor.ios_url")}
                        </label>
                        <Input
                          value={c.ios_url || ""}
                          onChange={(e) =>
                            updateCta(i, { ios_url: e.target.value || null })
                          }
                          className="h-9 rounded-lg"
                          placeholder="https://apps.apple.com/app/id…"
                          data-testid={`cta-ios-url-${i}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground font-medium">
                          {t("game_editor.android_url")}
                        </label>
                        <Input
                          value={c.android_url || ""}
                          onChange={(e) =>
                            updateCta(i, { android_url: e.target.value || null })
                          }
                          className="h-9 rounded-lg"
                          placeholder="https://play.google.com/store/apps/details?id=…"
                          data-testid={`cta-android-url-${i}`}
                        />
                      </div>
                      <div className="text-[10px] text-muted-foreground leading-relaxed">
                        {t("game_editor.app_urls_hint")}
                      </div>
                    </div>
                  )}
                  {i === 0 && c.type !== "app_download" && (
                    <div className="-mt-1 text-[10px] text-muted-foreground leading-relaxed">
                      {t("game_editor.url_hint")}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!c.required}
                      onCheckedChange={(v) => updateCta(i, { required: v })}
                      data-testid={`cta-required-${i}`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {t("game_editor.required")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <RulesDialog
        gameId={id}
        open={rulesOpen}
        onOpenChange={setRulesOpen}
      />
      <DuplicateGameDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        game={game}
        merchants={merchants}
        onConfirm={performDuplicate}
        loading={duplicateLoading}
      />
    </div>
  );
}
